import {computed, Injectable, signal} from '@angular/core';
import {ApiService} from '@core/services/api.service';
import {environment} from '@env/environment';
import {ICascadeLevel, IMasterAnnouncementFilter, IMasterAnnouncementLevel, IMasterElement, IMasterPermissionCategory, IMasterProduct, IMasterResponse, IMasterRole, IMasterTaxZone, IMasterTicketElement, IMasterTreeNode, IProviderFeatures, IProviderLegalLinks} from '@core/models/master.model';
import {UserRole} from '@core/models/user-info.model';
import {ISelect} from '@tecalisdev/infinity';
import {IResourceResponse, Status} from '@core/models/app.model';
import {
	allParentIds,
	hasParentId,
	isHierarchyRoot,
	primaryParentId,
	HierarchyParentNode,
} from '@shared/utils/hierarchy-parents.util';


@Injectable({
	providedIn: 'root'
})
export class MasterService extends ApiService {
	/** Fallback si /master aún no envía `tax_zones` enriquecidos (CHF-872 / TaxZone::Peninsula). */
	private static readonly FALLBACK_TAX_ZONE: IMasterTaxZone = {
		code: 'Península',
		label: 'Península',
		tax_name: 'IVA',
		rate: 0.21,
		is_default: true,
	};

	private masterSignal = signal<IMasterResponse>(null);
	/** Usuarios asignables (GET /users, acotado por rama en BE). No van en /master. */
	private usersSignal = signal<ISelect[]>([]);

	public master = computed(() => this.masterSignal());
	private previousUrl: string;

	public setMaster(value: IMasterResponse): void {
		this.masterSignal.set(value);
		this.loadUsers();
	}

	public getMaster() {
		return this.http.get<IResourceResponse<IMasterResponse>>(environment.apiUrl + `/master`, {headers: this.getHeaders()});
	}

	public refreshMaster(): void {
		this.getMaster().subscribe({
			next: (response) => this.setMaster({...response?.data}),
			error: (err: unknown) => console.error('[MasterService] refreshMaster error', err),
		});
	}

	private loadUsers(): void {
		this.http
			.get<{data?: {user_id?: string; name?: string; username?: string}[]}>(
				`${environment.apiUrl}/users`,
				{headers: this.getHeaders(), params: {limit: 100}},
			)
			.subscribe({
				next: (response) => {
					this.usersSignal.set(
						(response?.data ?? [])
							.filter((u): u is {user_id: string; name?: string; username?: string} => !!u.user_id)
							.map((u) => ({
								id: u.user_id,
								label: u.name || u.username || u.user_id,
							})),
					);
				},
				error: () => this.usersSignal.set([]),
			});
	}

	setPreviousUrl(previousUrl: string) {
		this.previousUrl = previousUrl;
	}

	getPreviousUrl(): string {
		return this.previousUrl;
	}

	masterToISelect(array: IMasterElement[] = []): ISelect[] {
		return array.map(({code, label}) => ({id: String(code), label}));
	}

	/**
	 * Convierte un array de IMasterTicketElement a ISelect[].
	 */
	ticketMasterToISelect(array: IMasterTicketElement[] = []): ISelect[] {
		return array.map(({code, label}) => ({id: code, label: label ?? code}));
	}

	getTicketTypes(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.ticket_types);
	}

	getTicketPriorities(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.ticket_priorities);
	}

	getTicketStatuses(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.ticket_statuses);
	}

	getTariffTypes(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.tariff_types);
	}

	getTariffSubtypes(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.tariff_subtypes);
	}

	getTariffPeriodicities(): ISelect[] {
		return this.ticketMasterToISelect(this.master()?.tariff_periodicities);
	}

	getMasterLabel(value: string, selects: ISelect[]): string {
		const found = selects?.find(status => status.id === value);
		return found ? found?.label : '';
	}

	getStatusLabel(value: string): string {
		const normalized = value === Status.INACTIVE ? Status.DEACTIVE : value;
		return this.getMasterLabel(normalized, this.getStatuses());
	}

	getStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.status);
	}

	getProductStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.product_status);
	}

	/**
	 * Estados de orden para filtros/UI.
	 * El BE ya omite estados de scoring si `configuration.scoring.enabled` es false.
	 */
	getOrderStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.order_statuses);
	}

	/** Orígenes de pedido (widget, panel, …) para filtros/UI. */
	getOrderOrigins(): ISelect[] {
		return this.masterToISelect(this.master()?.order_origins);
	}

	/**
	 * Enlaces CGC/privacidad del alta en panel (CHF-843).
	 * Misma shape que `widget_url.links`; el BE los resuelve en GET /master → `provider.legal_links`.
	 */
	getLegalLinks(): IProviderLegalLinks | null {
		const links = this.master()?.provider?.legal_links;
		if (!links || typeof links !== 'object' || Array.isArray(links)) {
			return null;
		}
		return links;
	}

	/**
	 * Feature flag del proveedor (`data.provider.features`).
	 * Path con puntos: `ui.alta`, `back.catalog`, `front.ftth`.
	 * Missing o falsy → false (oculto).
	 */
	hasFeature(path: string): boolean {
		const features = this.master()?.provider?.features;
		if (!features || !path) {
			return false;
		}
		return this.readFeatureFlag(features, path);
	}

	/** Niveles de comunicados activos para el provider (`null`/vacío = flujo legacy por canal). */
	hasAnnouncementLevels(): boolean {
		const levels = this.master()?.announcement_levels;
		return Array.isArray(levels) && levels.length > 0;
	}

	/** Options del select: `id` = slug de negocio (payload `level`). */
	getAnnouncementLevels(): ISelect[] {
		const levels = this.master()?.announcement_levels;
		if (!levels?.length) {
			return [];
		}
		return levels.map((level: IMasterAnnouncementLevel) => ({
			id: level.slug,
			label: level.name,
		}));
	}

	getAnnouncementLevelById(announcementLevelId: string | null | undefined): IMasterAnnouncementLevel | undefined {
		if (!announcementLevelId) {
			return undefined;
		}
		return (this.master()?.announcement_levels ?? []).find(
			(level) => level.code === announcementLevelId,
		);
	}

	/** Filtros de audiencia de comunicados (`null`/vacío = ocultar bloque en el form). */
	getAnnouncementFilters(): IMasterAnnouncementFilter[] {
		const filters = this.master()?.announcement_filters;
		if (!Array.isArray(filters) || !filters.length) {
			return [];
		}
		return filters.filter((f): f is IMasterAnnouncementFilter => !!f?.code);
	}

	hasAnnouncementFilters(): boolean {
		return this.getAnnouncementFilters().length > 0;
	}

	private readFeatureFlag(features: IProviderFeatures, path: string): boolean {
		const segments = path.split('.');
		let cursor: unknown = features;
		for (let i = 0; i < segments.length; i++) {
			if (cursor == null || typeof cursor !== 'object') {
				return false;
			}
			const record = cursor as Record<string, unknown>;
			// Claves planas con puntos en back/front (p. ej. `control.reports`), como Arr::get de Laravel.
			const rest = segments.slice(i).join('.');
			if (Object.prototype.hasOwnProperty.call(record, rest)) {
				return record[rest] === true;
			}
			cursor = record[segments[i]];
		}
		return cursor === true;
	}

	getWidgetApiKeys(): ISelect[] {
		return this.masterToISelect(this.master()?.widget_api_keys);
	}

	getServices(): ISelect[] {
		return this.masterToISelect(this.master()?.services);
	}

	getStructureElements(): ISelect[] {
		return (this.master()?.structure_elements || []).map((el: IMasterElement) => ({
			id: String(el.code ?? el.id ?? ''),
			label: el.label || el.name || '',
		}));
	}

	getStructureLevels(): ISelect[] {
		return this.masterToISelect(this.master()?.structure_levels);
	}

	getStructureLevelParents(): ISelect[] {
		const seen = new Set<string>();
		return (this.master()?.structure_levels || [])
			.map((sl: IMasterElement) => {
				const id = String(sl.code ?? '');
				return {
					id,
					label: sl.label || sl.name || '',
					parent_id: sl.parent_id,
					parent_ids: Array.isArray(sl.parent_ids) ? sl.parent_ids.map(String) : undefined,
				};
			})
			.filter((level) => {
				if (!level.id || seen.has(level.id)) {
					return false;
				}
				seen.add(level.id);
				return true;
			});
	}

	getStructureElementParents(structureId?: string): ISelect[] {
		const elements = this.master()?.structure_elements || [];
		const filtered = structureId
			? elements.filter((el: IMasterElement) => el.structure_id === structureId)
			: elements;
		return filtered.map((el: IMasterElement) => ({
			id: String(el.code),
			label: el.label || el.name || '',
			parent_id: el.parent_id,
			parent_ids: Array.isArray(el.parent_ids) ? el.parent_ids.map(String) : undefined,
		}));
	}

	getDocumentTypes(): ISelect[] {
		return (this.master()?.document_types || []).map((dt: IMasterElement) => ({
			id: dt.id,
			label: dt.label,
			value: dt.code,
		}));
	}


	getFiberDonorOperators(): ISelect[] {
		return (this.master()?.provider?.fiber_operators ?? []).map((d) => ({id: String(d.id), label: d.name}));
	}

	getMobileDonorOperators(): ISelect[] {
		return (this.master()?.provider?.mobile_operators ?? []).map((d) => ({id: String(d.id), label: d.name}));
	}

	getZones(): ISelect[] {
		return (this.master()?.zones ?? []).map((z: any) => ({
			id: String(z.zone_id ?? z.code),
			label: z.name || z.label || '',
		}));
	}

	/** Usuarios para selects (p. ej. responsable de lead). Fuente: GET /users. */
	getUsers(): ISelect[] {
		return this.usersSignal();
	}

	getLeadOrigin(): ISelect[] {
		return this.masterToISelect(this.master()?.lead?.origin);
	}

	getLeadStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.lead?.statuses);
	}

	getAlertSources(): ISelect[] {
		return this.masterToISelect(this.master()?.alerts?.sources);
	}

	getAlertRuleTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.alerts?.rule_types);
	}

	getResourceCategories(): ISelect[] {
		return this.masterToISelect(this.master()?.resource_categories);
	}

	getResourceStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.resource_statuses);
	}

	getResourceCategoryLabel(code?: string | null): string {
		return this.getMasterLabel(code ?? '', this.getResourceCategories()) || code || '';
	}

	getResourceStatusLabel(code?: string | null): string {
		return this.getMasterLabel(code ?? '', this.getResourceStatuses()) || code || '';
	}

	getContactRoles(): ISelect[] {
		return this.masterToISelect(this.master()?.contact_roles);
	}

	/** Referencia estable mientras no cambie `master` — evita que `tec-select` [search] resetee el filtro en cada CD. */
	private provincesISelect = computed((): ISelect[] =>
		(this.master()?.provinces ?? []).map((p: {province_id: string | number; name: string}) => ({
			id: String(p.province_id),
			label: p.name,
		})),
	);

	getProvinces(): ISelect[] {
		return this.provincesISelect();
	}


	getCountries(): ISelect[] {
		return (this.master()?.countries ?? []).map((c: any) => ({id: String(c.country_id), label: c.name}));
	}

	getFollowUpCommunicationTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.follow_up?.communication_types);
	}

	getRoles(): ISelect[] {
		return (this.master()?.roles || []).map((role: IMasterRole) => ({
			id: role.role_id,
			label: role.name,
		}));
	}

	getRoleMeta(roleId: string): IMasterRole | undefined {
		return (this.master()?.roles || []).find(r => r.role_id === roleId);
	}

	getRoleIdByCode(code: string): string | null {
		return (this.master()?.roles || []).find(r => r.code === code)?.role_id ?? null;
	}

	getStructureElementByCode(code: string): IMasterElement | undefined {
		return (this.master()?.structure_elements || []).find(el => String(el.code) === code);
	}

	getStructureLevelForElement(elementCode: string): ISelect | undefined {
		const el = this.getStructureElementByCode(elementCode);
		if (!el?.structure_id) return undefined;
		return this.getStructureLevelParents().find(l => l.id === el.structure_id);
	}

	getStructureLevelChain(levelId: string): string[] {
		const allLevels = this.getStructureLevelParents();
		const chain: string[] = [];
		const seen = new Set<string>();
		let id: string | undefined = levelId;
		while (id && !seen.has(id)) {
			seen.add(id);
			chain.unshift(id);
			const lvl = allLevels.find(l => l.id === id);
			id = primaryParentId(lvl as HierarchyParentNode) ?? undefined;
		}
		return chain;
	}

	/** Nivel hoja: tiene al menos un padre y ningún otro nivel lo lista en parent_ids. */
	isLowestStructureLevel(structureId: string): boolean {
		if (!structureId) return false;
		const levels = this.getStructureLevelParents();
		const level = levels.find(l => String(l.id) === String(structureId));
		if (!level || isHierarchyRoot(level as HierarchyParentNode)) return false;
		return !levels.some(l => hasParentId(l as HierarchyParentNode, structureId));
	}

	/** Id del nivel hoja (PDV), o null si master aún no tiene niveles. */
	getLowestStructureLevelId(): string | null {
		const levels = this.getStructureLevelParents();
		const leaf = levels.find(l => this.isLowestStructureLevel(String(l.id)));
		return leaf ? String(leaf.id) : null;
	}

	getStructureElementStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.structure_element_statuses)
			.filter((s) => String(s.id) !== 'baja');
	}

	/**
	 * Tipos custom del nivel de estructura (`master.structure_element_custom_types[structureId]`).
	 * Vacío / sin clave = no mostrar campos.
	 */
	getStructureElementCustomTypes(structureId?: string | null): ISelect[] {
		if (!structureId) return [];
		const byStructure = this.master()?.structure_element_custom_types;
		if (!byStructure || typeof byStructure !== 'object' || Array.isArray(byStructure)) {
			return [];
		}
		return this.masterToISelect(byStructure[String(structureId)] ?? []);
	}

	getPdvClusters(): ISelect[] {
		return this.masterToISelect(this.master()?.pdv?.clusters);
	}

	getPdvTypologies(): ISelect[] {
		return this.masterToISelect(this.master()?.pdv?.typologies);
	}

	getPdvPerformances(): ISelect[] {
		return this.masterToISelect(this.master()?.pdv?.performances);
	}

	/**
	 * Canales PDV (`master.pdv.channels`). id = `pdv_channel_id` (UUID), no el slug `code`.
	 * Vacío si el provider no tiene catálogo (p. ej. Audax). Catálogo completo (sin filtrar owner).
	 */
	getPdvChannels(): ISelect[] {
		return (this.master()?.pdv?.channels ?? []).map((c) => ({
			id: c.pdv_channel_id,
			label: c.name || c.code || '',
		}));
	}

	/**
	 * Canales PDV filtrados por mayorista (`owner_ids`).
	 * Con `ownerId`: canales que lo incluyen. Sin dueño (`null`): solo `owner_ids` vacío/ausente.
	 */
	getPdvChannelsForOwner(ownerId: string | null): ISelect[] {
		const channels = this.master()?.pdv?.channels ?? [];
		const filtered = ownerId
			? channels.filter((c) => (c.owner_ids ?? []).map(String).includes(String(ownerId)))
			: channels.filter((c) => !(c.owner_ids?.length));
		return filtered.map((c) => ({
			id: c.pdv_channel_id,
			label: c.name || c.code || '',
		}));
	}

	/** True si hay catálogo de canales PDV (CHF-753/754; hoy solo O2). */
	hasPdvChannels(): boolean {
		return (this.master()?.pdv?.channels ?? []).length > 0;
	}

	getPdvValidationCriteria(): {
		pdv_validation_criterion_id: string;
		code: string;
		label: string;
		severity: 'red' | 'yellow';
		sort_order?: number;
	}[] {
		return (this.master()?.pdv?.validation_criteria ?? []).map((c) => ({
			pdv_validation_criterion_id: c.pdv_validation_criterion_id,
			code: c.code,
			label: c.label,
			severity: c.severity,
			sort_order: c.sort_order,
		}));
	}

	getRegions(): ISelect[] {
		return (this.master()?.regions ?? []).map((r: any) => ({
			id: String(r.region_id ?? r.code),
			label: r.name || r.label || '',
		}));
	}

	getStructureElementsForLevel(levelId: string, parentId?: string | null): ISelect[] {
		const elements = this.master()?.structure_elements || [];
		return elements
			.filter((e: IMasterElement) => {
				const parentOk = !parentId || hasParentId(e, parentId);
				if (!parentOk) return false;
				// Master legacy sin structure_id: con parentId basta (hijos del Dist → tiendas).
				if (e.structure_id == null || e.structure_id === '') {
					return !!parentId;
				}
				return String(e.structure_id) === String(levelId);
			})
			.map((e: IMasterElement) => ({id: String(e.code), label: e.label || e.name || ''}));
	}

	/** Hijos directos de un SE en master (sin filtrar por nivel). */
	getStructureElementsByParent(parentId: string): ISelect[] {
		const elements = this.master()?.structure_elements || [];
		return elements
			.filter((e: IMasterElement) => hasParentId(e, parentId))
			.map((e: IMasterElement) => ({id: String(e.code), label: e.label || e.name || ''}));
	}

	getStructureElementAncestorAtLevel(elementCode: string, levelId: string): IMasterElement | undefined {
		const elements = this.master()?.structure_elements || [];
		let el = this.getStructureElementByCode(elementCode);
		const seen = new Set<string>();
		while (el) {
			const code = String(el.code);
			if (seen.has(code)) return undefined;
			seen.add(code);
			if (el.structure_id === levelId) return el;
			const parent = primaryParentId(el);
			if (!parent) return undefined;
			el = elements.find((e: IMasterElement) => String(e.code) === parent);
		}
		return undefined;
	}

	buildCascadeLevels(targetLevelId: string, preSelectElementId?: string): {levels: ICascadeLevel[]; options: ISelect[]} {
		const allLevels = this.getStructureLevelParents();
		const chain = this.getStructureLevelChain(targetLevelId);

		if (chain.length <= 1) {
			const options = this.getStructureElementsForLevel(targetLevelId);
			return {levels: [], options};
		}

		const levels: ICascadeLevel[] = chain.slice(0, -1).map((id, index) => ({
			levelId: id,
			label: this.getMasterLabel(id, allLevels),
			options: index === 0 ? this.getStructureElementsForLevel(id) : [],
			selected: null,
		}));

		if (!preSelectElementId) {
			return {levels, options: []};
		}

		for (let i = 0; i < levels.length; i++) {
			const ancestor = this.getStructureElementAncestorAtLevel(preSelectElementId, chain[i]);
			if (!ancestor) return {levels, options: []};
			const option = levels[i].options.find(o => o.id === String(ancestor.code));
			if (!option) return {levels, options: []};
			levels[i].selected = option;
			if (i + 1 < levels.length) {
				levels[i + 1].options = this.getStructureElementsForLevel(chain[i + 1], option.id as string);
			}
		}

		const lastSelected = levels[levels.length - 1].selected;
		const options = lastSelected?.id
			? this.getStructureElementsForLevel(targetLevelId, lastSelected.id as string)
			: [];

		return {levels, options};
	}

	/**
	 * Rol master ligado a un nivel de estructura:
	 * `has_structure_element` y `name` coincide con el label del nivel.
	 */
	getRoleForStructureLevel(structureId: string | null | undefined): IMasterRole | null {
		if (!structureId) return null;
		const level = this.getStructureLevelParents().find(
			(l) => String(l.id) === String(structureId),
		);
		if (!level?.label) return null;
		const levelLabel = String(level.label).toLowerCase();
		return (this.master()?.roles || []).find(
			(r) => r.has_structure_element && r.name?.toLowerCase() === levelLabel,
		) ?? null;
	}

	/** True si el nivel está asociado al rol `technical_sales` (TC). */
	isTechnicalSalesStructureLevel(structureId: string | null | undefined): boolean {
		return this.getRoleForStructureLevel(structureId)?.code === UserRole.TECHNICAL_SALES;
	}

	/**
	 * True si el nivel está asociado al rol `gpv`.
	 * El name del rol O2 es "GPV (gestor…)" y el label del nivel es "GPV", así que
	 * no basta el match exacto de `getRoleForStructureLevel`.
	 */
	isGpvStructureLevel(structureId: string | null | undefined): boolean {
		if (!structureId) return false;
		const level = this.getStructureLevelParents().find(
			(l) => String(l.id) === String(structureId),
		);
		if (!level?.label) return false;
		const levelLabel = String(level.label).toLowerCase().trim();
		return (this.master()?.roles || []).some((r) => {
			if (r.code !== UserRole.GPV || !r.has_structure_element) return false;
			const roleName = r.name?.toLowerCase().trim() ?? '';
			return roleName === levelLabel || roleName.startsWith(levelLabel);
		});
	}

	/**
	 * True si el nivel está asociado al rol `distributor` (mayorista).
	 * Misma flexibilidad de label/name que GPV (p. ej. «Mayorista» vs name del rol).
	 */
	isDistributorStructureLevel(structureId: string | null | undefined): boolean {
		if (!structureId) return false;
		const byExact = this.getRoleForStructureLevel(structureId);
		if (byExact?.code === UserRole.DISTRIBUTOR) return true;
		const level = this.getStructureLevelParents().find(
			(l) => String(l.id) === String(structureId),
		);
		if (!level?.label) return false;
		const levelLabel = String(level.label).toLowerCase().trim();
		return (this.master()?.roles || []).some((r) => {
			if (r.code !== UserRole.DISTRIBUTOR || !r.has_structure_element) return false;
			const roleName = r.name?.toLowerCase().trim() ?? '';
			return roleName === levelLabel
				|| roleName.startsWith(levelLabel)
				|| levelLabel.startsWith(roleName);
		});
	}

	getRoleIdForStructureElement(elementCode: string): string | null {
		const level = this.getStructureLevelForElement(elementCode);
		if (!level?.id) return null;
		return this.getRoleForStructureLevel(String(level.id))?.role_id ?? null;
	}

	getPermissionCategories(): ISelect[] {
		return (this.master()?.permission_categories || []).map((cat: IMasterPermissionCategory) => ({
			id: cat.code,
			label: cat.name,
		}));
	}

	getReportStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.report_status);
	}

	getReportTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.report_types);
	}

	getLockTypes(): IMasterElement[] {
		return this.master()?.lock_types || [];
	}

	getLockTypesData(): ISelect[] {
		return this.masterToISelect(this.master()?.lock_types_data);
	}

	getLockTypesDataLabel(code: string): string {
		return this.getMasterLabel(code, this.getLockTypesData());
	}

	getPortabilityStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.portability_status);
	}

	getStreetTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.street_types);
	}

	getBarringsCall118xx(): ISelect[] {
		return this.masterToISelect(this.master()?.barrings?.call_118xx);
	}

	getBillingPayTypeTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.billing?.pay_type_types);
	}

	getBillingDeadlines(): ISelect[] {
		return this.masterToISelect(this.master()?.billing?.deadlines);
	}

	getBillingRecordStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.billing?.record_statuses);
	}

	getBillingInvoiceStatuses(): ISelect[] {
		return this.masterToISelect(this.master()?.billing?.invoice_statuses);
	}

	getBillingInvoiceStatusLabel(code: string | null | undefined): string {
		return this.getMasterLabel(code ?? '', this.getBillingInvoiceStatuses()) || code || '-';
	}

	getPostsellReasons(): ISelect[] {
		return this.masterToISelect(this.master()?.postsells_reasons);
	}

	getCdrTypes(): ISelect[] {
		return this.masterToISelect(this.master()?.cdr_types);
	}

	getCancellationReasons(): ISelect[] {
		return this.masterToISelect(this.master()?.cancellation_reasons);
	}

	getProducts(): ISelect[] {
		return this.master()?.products?.map((p: IMasterProduct) => ({
			id: String(p.product_id),
			label: p.name,
		})) ?? [];
	}

	/**
	 * Zonas fiscales de master (CHF-872). Normaliza legacy `string[]` → objetos mínimos.
	 */
	getTaxZones(): IMasterTaxZone[] {
		const raw = this.master()?.tax_zones;
		if (!raw?.length) return [];
		if (typeof raw[0] === 'string') {
			return (raw as string[]).map((code) => ({
				code,
				label: code,
				tax_name: code === 'Península' ? 'IVA' : code === 'Canarias' ? 'IGIC' : 'IPSI',
				rate: code === 'Península' ? 0.21 : code === 'Canarias' ? 0.07 : code === 'Ceuta' ? 0.1 : 0.08,
				is_default: code === 'Península',
			}));
		}
		return raw as IMasterTaxZone[];
	}

	/** Zona default de ventas (Península / IVA 21 %) hasta criterio IGIC por dirección. */
	getDefaultTaxZone(): IMasterTaxZone {
		const zones = this.getTaxZones();
		return zones.find((z) => z.is_default) ?? zones.find((z) => z.code === 'Península') ?? MasterService.FALLBACK_TAX_ZONE;
	}

	getDefaultTaxRate(): number {
		const rate = this.getDefaultTaxZone().rate;
		return typeof rate === 'number' && !isNaN(rate) ? rate : 0.21;
	}

	getDefaultTaxName(): string {
		return this.getDefaultTaxZone().tax_name || 'IVA';
	}

	/**
	 * Construye el subárbol de structure elements a partir de un elemento raíz,
	 * usando los datos del master (sin llamada API adicional).
	 * Hijos = cualquier nodo cuyo parent_ids (o parent_id BC) incluya al padre.
	 * @param rootElementId  structure_element_id del elemento raíz (el distribuidor actual)
	 */
	buildStructureElementTree(rootElementId: string): IMasterTreeNode[] {
		const all = this.master()?.structure_elements || [];

		const buildLevel = (parentId: string, visited: Set<string>): IMasterTreeNode[] =>
			all
				.filter(el => hasParentId(el, parentId) && !visited.has(String(el.code)))
				.map(el => {
					const id = String(el.code);
					const nextVisited = new Set(visited);
					nextVisited.add(id);
					return {
						id,
						label: el.name || el.label || '',
						structureId: el.structure_id ? String(el.structure_id) : undefined,
						children: buildLevel(id, nextVisited),
					};
				});

		return buildLevel(rootElementId, new Set([rootElementId]));
	}

	/**
	 * Bosque completo de structure_elements (raíces = sin padres o padres fuera del master).
	 * Usado por HierarchySelector (alcance de comunicados/banners/newsletters).
	 */
	buildStructureElementForest(): IMasterTreeNode[] {
		const all = this.master()?.structure_elements || [];
		const codes = new Set(all.map((el) => String(el.code)));
		const roots = all.filter((el) => {
			const parents = allParentIds(el);
			return parents.length === 0 || parents.every((p) => !codes.has(p));
		});

		return roots.map((el) => ({
			id: String(el.code),
			label: el.name || el.label || '',
			structureId: el.structure_id ? String(el.structure_id) : undefined,
			children: this.buildStructureElementTree(String(el.code)),
		}));
	}

	/**
	 * Devuelve el label del nivel de estructura (ej: "Tienda", "Comercial")
	 * dado su structure_id (= structure_levels.code).
	 */
	getStructureLevelLabel(structureId: string): string {
		const level = (this.master()?.structure_levels || [])
			.find(sl => String(sl.code) === structureId);
		return level?.label || level?.name || '';
	}

}
